// Sentry initialization must run before React mounts so the error
// boundary captures startup errors. Module side-effect import; the file
// is a no-op when VITE_SENTRY_DSN is unset.
import './sentry';

// CookieYes consent-management loader. Env-gated (prod) module side-effect;
// no-op when VITE_COOKIEYES_SITE_ID is unset.
import './cookieyes';

// Google Tag Manager loader. Env-gated (prod) module side-effect; no-op
// when VITE_GTM_ID is unset. Imported after `./cookieyes` so the consent
// script loads first and can gate the tags GTM fires.
import './gtm';

// Stale-bundle recovery. After a deploy, a still-open tab's index.html
// references the previous build's chunk hashes (e.g. `index-5GmBedkL.js`);
// the server no longer has those files. Any navigation that triggers a
// dynamic import (route lazy-load, code-split component) attempts to
// fetch the missing chunk and fails. The reload mechanics live in
// `./lib/stale-bundle` so the locale catalog loader can share them.
//
// Two listeners, two coverage shapes:
//
//   1. `vite:preloadError` — fired by Vite's own `__vitePreload` wrapper
//      when the import it dispatched 404s. Covers route-tree imports the
//      Vite build-time transform wraps. The wrapper re-throws the error
//      after firing the event, so without this listener TanStack Router's
//      CatchBoundary would render "Something went wrong!".
//
//   2. `unhandledrejection` — fired by the browser for any promise reject
//      that nothing else handles. Covers dynamic imports that DIDN'T go
//      through `__vitePreload`: TanStack's own preload paths, runtime
//      `import()` from app code that wasn't transformed, browser-native
//      module resolution, and cases where the wrapper's event was
//      preventDefault'd downstream.
import { isStaleBundleError, reloadOnceForStaleBundle } from './lib/stale-bundle';

window.addEventListener('vite:preloadError', () => reloadOnceForStaleBundle());
window.addEventListener('unhandledrejection', (event) => {
  if (isStaleBundleError(event.reason)) reloadOnceForStaleBundle();
});

import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { LoadingState } from '@leanin/ui';

import { routeTree } from './routeTree.gen';
import { LocaleBoundary } from './i18n/LocaleBoundary';
import { detectBrowserLocale } from './i18n/detect';
import { useSession } from './lib/use-session';
import '@leanin/ui/tokens.css';
import './globals.scss';
import { NotFoundPage } from './components/shell/NotFoundPage';
import { MessagingDockProvider } from './components/messaging-dock';
import { isNotFoundApiError } from './lib/api-error';
import { setApiTokenGetter } from './lib/api-client';
import { setAnalyticsUserId } from './lib/analytics/data-layer';
import { trackPageViewed } from './lib/analytics/events';
import { resolvePageMeta, type PageIdLoaderData } from './lib/analytics/page-meta';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 404 is a permanent state — retrying just wastes time and keeps
      // detail pages in a skeleton/retry state for several seconds before
      // they can render the NotFoundPage. Other errors still get the
      // default 3 retries.
      retry: (failureCount, err) =>
        !isNotFoundApiError(err) && failureCount < 3,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient, member: null },
  // The app's scroll container is AppShell's <main> (overflow-y: auto), not
  // the window. Without these, navigating to a new page leaves <main> scrolled
  // wherever the previous page left it.
  scrollRestoration: true,
  scrollToTopSelectors: ['main'],
  // Branded 404 surface for any route that misses (manual URL typos, stale
  // bookmarks, dynamic routes that capture a non-matching segment like
  // `/profile/edit` -> `/profile/$id` with id='edit' -> notFound). Without
  // this the framework default is a bare `<p>Not Found</p>`.
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// GA4 page_viewed for the SPA. Module-scope subscription (not a component
// effect) so the router's INITIAL resolution is captured too — a mount
// effect races it. `onResolved` fires once per committed navigation, after
// guards/redirect chains settle; `onBeforeLoad` would count destinations
// the user never reached (e.g. `_authed`'s login bounce). The GTM container
// must have Enhanced Measurement history-based page views DISABLED or every
// navigation double-counts.
let lastTrackedHref: string | null = null;
router.subscribe('onResolved', ({ toLocation }) => {
  if (toLocation.href === lastTrackedHref) return;
  lastTrackedHref = toLocation.href;
  const leaf = router.state.matches[router.state.matches.length - 1];
  const params = (leaf?.params ?? {}) as Record<string, string>;
  const loaderData = leaf?.loaderData as PageIdLoaderData | undefined;
  trackPageViewed(resolvePageMeta(toLocation.pathname, params, loaderData));
});

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '';

// Router boot is gated on Clerk readiness — the standard Clerk SPA boot
// pattern. The router's initial navigation runs route guards whose session
// probe must carry `Authorization: Bearer <fresh token>`: Clerk's
// `__session` cookie is a ~60s JWT, stale for any returning visitor, so a
// probe that rides the cookie alone gets 401 and the guard bounces a
// genuinely signed-in user to /auth/login.
//
// Ordering guarantee: the same effect that registers the token getter
// flips `routerReady`, so by the time <RouterProvider> mounts (the render
// AFTER that effect committed) the api-client getter is in place — the
// first session probe always carries the header.
//
// Bounded wait: if Clerk JS never loads (clerk.com blocked/unreachable),
// `isLoaded` stays false. After CLERK_LOAD_TIMEOUT_MS the router mounts
// anyway: the probe runs tokenless and the route guards surface their
// retryable-error / login states instead of a permanently stuck splash.
// Should Clerk finish loading later, the first effect registers the
// getter at that point.
//
// Local dev: `isLoaded` flips true for signed-out clients too (the SDK
// reports loaded once initialized, session or not), so the dev-bypass
// flow (dev_user_id cookie → X-Dev-User-Id header in authFetch) is never
// blocked by this gate. Must render under ClerkProvider.
const CLERK_LOAD_TIMEOUT_MS = 8_000;

function ClerkRouterGate() {
  const { isLoaded, getToken } = useAuth();
  const [routerReady, setRouterReady] = useState(false);

  useEffect(() => {
    if (!isLoaded) return undefined;
    setApiTokenGetter(() => getToken());
    setRouterReady(true);
    return () => setApiTokenGetter(null);
  }, [isLoaded, getToken]);

  useEffect(() => {
    if (isLoaded) return undefined;
    const timer = setTimeout(() => {
      console.warn(
        `Clerk did not load within ${CLERK_LOAD_TIMEOUT_MS}ms — mounting the router without a session token getter`,
      );
      setRouterReady(true);
    }, CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoaded]);

  if (!routerReady) {
    return <LoadingState placement="page" label="Loading" announceOn="mount" />;
  }
  return (
    <AppLocaleProvider>
      <AnalyticsUserSync />
      {/* Above the router so the messaging dock's open/collapsed state survives
          every navigation, including the `_authed ↔ _public` Shell remount. */}
      <MessagingDockProvider>
        <RouterProvider router={router} />
      </MessagingDockProvider>
    </AppLocaleProvider>
  );
}

// Binds the GA4 user_id to the INTERNAL session user id (never Clerk's).
// Lives beside AppLocaleProvider — above BOTH route trees — so a signed-in
// member browsing `_public` pages is still identified, and sign-out (the
// session query resolving null) clears the id. Waits for the probe to
// settle so a loading state isn't reported as signed-out.
function AnalyticsUserSync() {
  const { data: ctx, isPending } = useSession();
  const userId = ctx?.session.userId ?? null;
  useEffect(() => {
    if (isPending) return;
    setAnalyticsUserId(userId);
  }, [isPending, userId]);
  return null;
}

// Single locale provider for the whole app. Uses the signed-in member's
// stored locale when a session exists (so it's consistent across BOTH the
// `_authed` and `_public` route trees — a logged-in member browsing a public
// page still sees their language), falling back to the browser-detected
// locale for anonymous visitors. Lives inside ClerkRouterGate so it reads the
// session only after the API token getter is wired, and wraps the router so
// navigating between route trees can't flip the language.
function AppLocaleProvider({ children }: { children: ReactNode }) {
  const { data: ctx } = useSession();
  const locale = ctx?.member.locale ?? detectBrowserLocale();
  return <LocaleBoundary locale={locale}>{children}</LocaleBoundary>;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

// Block initial React render until the critical brand fonts are loaded,
// so the first paint already uses Avenir Next / Newsreader — no
// glyph-shape swap when the brand font lands a few hundred ms in.
//
// Why the FontFace-constructor approach (not document.fonts.load by
// descriptor): in Vite dev mode the SCSS is injected via a JS-side
// <style> element which the browser parses asynchronously. Calling
// `document.fonts.load('400 14px "Avenir Next"')` at this point
// short-circuits — no matching @font-face is registered yet, the API
// resolves with an empty array immediately, and we'd render with
// fallback. Constructing FontFace objects directly bypasses that race:
// each face fetches from its URL whether or not the CSS has been
// parsed yet, and the resulting FontFace becomes the canonical entry
// in document.fonts for that family/weight combination.
//
// The URLs and weight descriptors here MUST mirror the @font-face
// declarations in globals.scss exactly (same family name, same weight
// range) so the CSS-side declaration doesn't introduce a duplicate
// face that competes with the JS-loaded one.
//
// Hard cap at 1200ms: a missing or genuinely slow font can't block the
// app forever. Past the cap we paint with the metric-matched fallback
// declared in globals.scss; the real font swaps in when it eventually
// arrives. Pattern matches Linear / Vercel dashboard / Notion's
// "block-then-bail" first-paint gating.
const FONT_READY_TIMEOUT_MS = 1200;

const FIRST_PAINT_FONTS: ReadonlyArray<{
  family: string;
  url: string;
  weight: string;
}> = [
  // Body / closed composer prompt / Caption — every page's running text.
  { family: 'Avenir Next', url: '/fonts/AvenirNext-Regular.woff2', weight: '400' },
  // Label / Button / Pill / Tab pill / Field label — every UI control.
  // Weight range covers the design system's `--type-label-weight: 600`
  // and any direct `700` request through the same Demi file.
  { family: 'Avenir Next', url: '/fonts/AvenirNext-Demi.woff2', weight: '600 700' },
  // Heading / Display — page titles, panel headers, hero text.
  {
    family: 'Newsreader',
    url: "/fonts/Newsreader-VariableFont_opsz,wght.woff2",
    weight: '200 800',
  },
];

async function awaitCriticalFonts(): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;
  if (typeof FontFace === 'undefined') return;
  const loadAll = Promise.all(
    FIRST_PAINT_FONTS.map(async ({ family, url, weight }) => {
      const face = new FontFace(family, `url(${url}) format('woff2')`, {
        weight,
        style: 'normal',
        display: 'swap',
      });
      const loaded = await face.load();
      document.fonts.add(loaded);
      return loaded;
    }),
  );
  const timeout = new Promise<void>((resolve) =>
    setTimeout(resolve, FONT_READY_TIMEOUT_MS),
  );
  await Promise.race([loadAll, timeout]);
}

// ClerkProvider is mounted unconditionally. The dev-bypass flag
// (LEANIN_ENV=local + DEV_AUTH_BYPASS=1) is a *backend* shortcut that
// honours an `X-Dev-User-Id` header; the frontend still owns the real
// sign-in / sign-up / 2FA flows, all of which call Clerk hooks. Skipping
// the provider in local-dev crashed those pages with "useUser /
// useSignIn can only be used within the <ClerkProvider />" the moment
// anyone navigated to /auth/* or /settings/sessions/*.
void awaitCriticalFonts().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <ClerkProvider publishableKey={clerkPublishableKey}>
        <QueryClientProvider client={queryClient}>
          <ClerkRouterGate />
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>,
  );
});
